import { Check, Clock, IndianRupee, MapPinned, Minus, ShieldCheck, Truck, X } from "lucide-react";
import PageHead from "../components/PageHead";
import { Reveal, SectionHead } from "../components/Reveal";
import CtaBand from "../components/CtaBand";

const PILLARS = [
    {
        icon: MapPinned,
        title: "Pan India Service",
        hindi: "Service across Pan India",
        desc: "Every state, every major corridor — one supplier for all your lanes, not just one region.",
        span: "lg:col-span-2",
    },
    {
        icon: IndianRupee,
        title: "Competitive Rates",
        hindi: "Competitive pricing",
        desc: "Direct depot routing keeps freight rates sharp and transparent — no layered commissions.",
        span: "lg:col-span-2",
    },
    {
        icon: Truck,
        title: "Reliable Vehicle Placement",
        hindi: "Reliable vehicle placement",
        desc: "Trailers placed when promised — 40ft, 50ft or 60ft, at the ramp you need.",
        span: "lg:col-span-2",
    },
    {
        icon: Clock,
        title: "Timely Transportation",
        hindi: "On-time transportation",
        desc: "Committed timelines from loading to delivery — cargo moves when it should.",
        span: "lg:col-span-3",
    },
    {
        icon: ShieldCheck,
        title: "Professional Service",
        hindi: "Professional service",
        desc: "Owner-led, accountable and easy to work with — One Stop Movers, every time.",
        span: "lg:col-span-3",
    },
];

const COMPARE = [
    { row: "Coverage", us: "Pan India trailer supply", them: "Regional lanes only" },
    { row: "Vehicle placement", us: "Direct depot routing, on call", them: "Uncertain, middlemen-dependent" },
    { row: "Rates", us: "Competitive and transparent", them: "Layered commissions" },
    { row: "Timelines", us: "Committed, kept", them: "Verbal assurances" },
    { row: "Service", us: "Owner-led and professional", them: "Varies load to load" },
];

export default function WhyUs() {
    return (
        <>
            <PageHead
                eyebrow="Why Choose Us"
                title="Five reasons shippers stay"
                hindi="Why us? Here's why."
                intro="In a market full of brokers and broken promises, OSM Cargo Movers runs on five commitments — and one owner who answers the phone."
                testid="why-us-page"
            />

            <section className="pb-20 lg:pb-28" data-testid="why-pillars-section">
                <div className="container-x grid gap-5 sm:grid-cols-2 lg:grid-cols-6">
                    {PILLARS.map((p, i) => (
                        <Reveal
                            key={p.title}
                            delay={i * 0.06}
                            className={`h-full ${p.span} ${i > 2 ? "sm:col-span-2" : ""}`}
                        >
                            <div className="card group flex h-full flex-col p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-ink hover:shadow-xl hover:shadow-ink/5">
                                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 transition-colors group-hover:bg-brand">
                                    <p.icon className="h-5 w-5 text-brand transition-colors group-hover:text-white" />
                                </span>
                                <h3 className="mt-6 font-display text-xl font-bold uppercase tracking-tight text-ink">
                                    {p.title}
                                </h3>
                                <p className="mt-1 font-hindi text-sm text-steel">{p.hindi}</p>
                                <p className="mt-3 text-sm leading-relaxed text-steel">{p.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-sand py-20 lg:py-28" data-testid="why-compare-section">
                <div className="container-x">
                    <SectionHead
                        eyebrow="The Difference"
                        title="OSM vs. the usual broker"
                        hindi="The difference is clear"
                    />
                    <Reveal delay={0.1}>
                        <div className="mt-12 overflow-hidden rounded-3xl border border-line bg-white" data-testid="comparison-table">
                            <div className="grid grid-cols-[1fr_1.2fr_1.2fr] border-b border-line bg-navy text-paper">
                                <div className="p-4 sm:p-5" />
                                <div className="border-l border-paper/10 p-4 sm:p-5">
                                    <p className="font-display text-sm font-bold uppercase tracking-tight sm:text-base">
                                        OSM Cargo Movers
                                    </p>
                                </div>
                                <div className="border-l border-paper/10 p-4 sm:p-5">
                                    <p className="font-display text-sm font-bold uppercase tracking-tight text-paper/60 sm:text-base">
                                        Typical local broker
                                    </p>
                                </div>
                            </div>
                            {COMPARE.map((c) => (
                                <div
                                    key={c.row}
                                    className="grid grid-cols-[1fr_1.2fr_1.2fr] border-b border-line last:border-b-0"
                                >
                                    <div className="p-4 sm:p-5">
                                        <p className="text-xs font-bold uppercase tracking-[0.12em] text-steel sm:text-sm">
                                            {c.row}
                                        </p>
                                    </div>
                                    <div className="border-l border-line p-4 sm:p-5">
                                        <p className="flex items-start gap-2 text-xs leading-relaxed text-ink sm:text-sm">
                                            <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#10B981]" />
                                            {c.us}
                                        </p>
                                    </div>
                                    <div className="border-l border-line p-4 sm:p-5">
                                        <p className="flex items-start gap-2 text-xs leading-relaxed text-fog sm:text-sm">
                                            <span className="flex gap-0.5">
                                                <Minus className="mt-0.5 h-3 w-3 shrink-0" />
                                                <X className="mt-0.5 h-3 w-3 shrink-0" />
                                            </span>
                                            {c.them}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand title="Ship with confidence" hindi="Ship with confidence — today." />
        </>
    );
}
